import { useEffect, useId, useMemo, useState } from "react";
import { ArrowRight, Filter, X } from "lucide-react";
import {
  acceptedProjectImages,
  filterProjects,
  portfolioLocations,
  portfolioRoofTypes,
  publishableProjects,
  type BeforeAfterProject,
} from "@/config/projects";
import { findServiceArea } from "@/config/service-areas";
import { publishedServicePages, serviceBySlug } from "@/config/services";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { localizeServiceArea, localizeServicePage } from "@/i18n/content";
import { getMarketingCopy, type MarketingCopy } from "@/i18n/marketing-copy";
import { localizeProject } from "@/i18n/project-content";
import type { LocaleCode } from "@/i18n/locale-config";

type PathBuilder = (href: string) => string;

const ALL = "";
const PAGE_SIZE = 9;

function serviceLabel(slug: string | null, locale: LocaleCode, uncategorized: string): string {
  if (!slug) return uncategorized;
  const service = serviceBySlug[slug];
  const localized = service ? localizeServicePage(service, locale) : undefined;
  return localized?.shortTitle ?? localized?.title ?? slug;
}

function cityLabel(cityId: string, locale: LocaleCode): string {
  const area = findServiceArea(cityId);
  return area ? localizeServiceArea(area, locale).name : cityId;
}

function roofTypeLabel(value: string, locale: LocaleCode): string {
  if (locale === "fa-ir") {
    return (
      {
        "Architectural asphalt": "شینگل آسفالتی معماری",
        "Low-slope membrane": "غشای سقف کم‌شیب",
        "Eave drainage": "زهکشی لبهٔ بام",
        "Cedar shake": "سقف چوبی سدار",
        "Ribbed metal": "سقف فلزی پروفیل‌دار",
        "Standing-seam metal": "سقف فلزی درز ایستاده",
        "Low-slope SBS membrane": "غشای SBS کم‌شیب",
        "Low-slope TPO membrane": "غشای TPO کم‌شیب",
        "Eave components": "اجزای لبهٔ بام",
        "Damaged steep-slope": "سقف شیب‌دار آسیب‌دیده",
      }[value] ?? value
    );
  }
  if (locale === "ar-sa") {
    return (
      {
        "Architectural asphalt": "ألواح شينجل أسفلتية معمارية",
        "Low-slope membrane": "غشاء سقف منخفض الميل",
        "Eave drainage": "تصريف حافة السقف",
        "Cedar shake": "ألواح خشب الأرز",
        "Ribbed metal": "سقف معدني مضلّع",
        "Standing-seam metal": "سقف معدني بدرزات قائمة",
        "Low-slope SBS membrane": "غشاء SBS منخفض الميل",
        "Low-slope TPO membrane": "غشاء TPO منخفض الميل",
        "Eave components": "مكونات حافة السقف",
        "Damaged steep-slope": "سقف شديد الميل متضرر",
      }[value] ?? value
    );
  }
  return value;
}

/**
 * Filterable portfolio grid for /before-after.
 * Only publishable projects (accepted before + after) appear; thumbnails lazy-load.
 */
export function PortfolioGallery({
  path,
  projects,
  className,
}: {
  path: PathBuilder;
  projects?: readonly BeforeAfterProject[];
  className?: string;
}) {
  const { locale } = useI18n();
  const copy = getMarketingCopy(locale)?.portfolio;
  const baseId = useId();
  const [service, setService] = useState(ALL);
  const [roofType, setRoofType] = useState(ALL);
  const [city, setCity] = useState(ALL);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const sourceProjects = useMemo(
    () => (projects ? publishableProjects(projects) : publishableProjects()),
    [projects],
  );

  const roofTypes = useMemo(() => portfolioRoofTypes(), []);
  const locations = useMemo(() => portfolioLocations(), []);
  const serviceOptions = useMemo(() => {
    const present = new Set(
      sourceProjects.map((p) => p.service).filter((value): value is string => Boolean(value)),
    );
    return publishedServicePages
      .filter((s) => present.has(s.slug))
      .map((s) => {
        const localized = localizeServicePage(s, locale);
        return { value: s.slug, label: localized.shortTitle || localized.title };
      });
  }, [sourceProjects, locale]);

  const filtered = useMemo(() => {
    const filters: { service?: string; roofType?: string; city?: string } = {};
    if (service) filters.service = service;
    if (roofType) filters.roofType = roofType;
    if (city) filters.city = city;
    const within = filterProjects(filters);
    const allowed = new Set(sourceProjects.map((p) => p.id));
    return within.filter((p) => allowed.has(p.id));
  }, [service, roofType, city, sourceProjects]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [service, roofType, city]);

  const visible = filtered
    .slice(0, visibleCount)
    .map((project) => localizeProject(project, locale));
  const hasMore = visibleCount < filtered.length;
  const hasActiveFilters = Boolean(service || roofType || city);

  const clearFilters = () => {
    setService(ALL);
    setRoofType(ALL);
    setCity(ALL);
  };

  return (
    <section className={cn("section-y pt-0", className)} aria-labelledby={`${baseId}-heading`}>
      <div className="container-page">
        <div className="rounded-xl border border-border bg-muted/35 p-5 md:p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.14em] uppercase text-muted-foreground">
                <Filter className="size-3.5" aria-hidden="true" />
                {copy?.filterEyebrow ?? "Filter galleries"}
              </p>
              <h2 id={`${baseId}-heading`} className="mt-2 text-lg font-semibold tracking-tight">
                {copy?.filterTitle ?? "Before & after gallery"}
              </h2>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                {copy?.filterBody ??
                  "Filter by service, roof type, or city to browse the galleries below."}
              </p>
            </div>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="size-3.5" aria-hidden="true" />
                {copy?.clearFilters ?? "Clear filters"}
              </button>
            ) : null}
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor={`${baseId}-service`}>{copy?.service ?? "Service"}</Label>
              <select
                id={`${baseId}-service`}
                value={service}
                onChange={(e) => setService(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value={ALL}>{copy?.allServices ?? "All services"}</option>
                {serviceOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${baseId}-roof`}>{copy?.roofType ?? "Roof type"}</Label>
              <select
                id={`${baseId}-roof`}
                value={roofType}
                onChange={(e) => setRoofType(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value={ALL}>{copy?.allRoofTypes ?? "All roof types"}</option>
                {roofTypes.map((type) => (
                  <option key={type} value={type}>
                    {roofTypeLabel(type, locale)}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${baseId}-city`}>{copy?.city ?? "City"}</Label>
              <select
                id={`${baseId}-city`}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value={ALL}>{copy?.allCities ?? "All cities"}</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>
                    {cityLabel(loc, locale)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p className="mt-4 text-sm text-muted-foreground" aria-live="polite">
            {copy?.showing(
              Math.min(visibleCount, filtered.length),
              filtered.length,
              hasActiveFilters,
              sourceProjects.length,
            ) ??
              `Showing ${Math.min(visibleCount, filtered.length)} of ${filtered.length} galleries${hasActiveFilters ? " (filtered)" : ""}${filtered.length !== sourceProjects.length ? ` · ${sourceProjects.length} total` : ""}.`}
          </p>
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10 rounded-xl border border-dashed border-border bg-muted/20 px-6 py-14 text-center">
            <p className="text-base font-medium text-foreground">
              {copy?.noMatchesTitle ?? "No galleries match these filters"}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {copy?.noMatchesBody ??
                "Clear filters or choose a different service, roof type or city style."}
            </p>
            <button
              type="button"
              onClick={clearFilters}
              className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {copy?.resetFilters ?? "Reset filters"}
            </button>
          </div>
        ) : (
          <>
            <ul className="mt-10 grid gap-8 sm:grid-cols-2 xl:grid-cols-3">
              {visible.map((project, index) => (
                <li key={project.id}>
                  <PortfolioCard
                    project={project}
                    path={path}
                    eager={index < 3}
                    locale={locale}
                    copy={copy}
                  />
                </li>
              ))}
            </ul>
            {hasMore ? (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {copy?.loadMore ?? "Load more galleries"}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

function PortfolioCard({
  project,
  path,
  eager,
  locale,
  copy,
}: {
  project: BeforeAfterProject;
  path: PathBuilder;
  eager: boolean;
  locale: LocaleCode;
  copy?: MarketingCopy["portfolio"] | undefined;
}) {
  const href = path(`/before-after/${project.slug}`);
  const serviceName = serviceLabel(project.service, locale, copy?.uncategorized ?? "Uncategorized");
  const acceptedCount =
    acceptedProjectImages(project.beforeImages).length +
    acceptedProjectImages(project.afterImages).length;
  const cityName = project.city ? cityLabel(project.city, locale) : null;

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md">
      <a
        href={href}
        className="relative block overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <div className="relative aspect-[3/2] bg-muted">
          <img
            src={project.featuredImage.src}
            alt={project.featuredImage.alt}
            width={project.width}
            height={project.height}
            loading={eager ? "eager" : "lazy"}
            decoding="async"
            className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-deep/55 via-transparent to-transparent" />
          <span className="absolute bottom-3 left-3 right-3 text-xs font-medium text-primary-foreground/90">
            {cityName ?? project.location}
          </span>
        </div>
      </a>

      <div className="flex flex-1 flex-col p-5">
        <p className="text-xs tracking-[0.12em] uppercase text-muted-foreground">{serviceName}</p>
        <h3 className="mt-2 text-base font-semibold tracking-tight text-foreground">
          <a
            href={href}
            className="underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {project.title}
          </a>
        </h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
          {project.shortProblem}
        </p>
        <p className="mt-3 text-xs text-muted-foreground">
          {project.roofType ?? project.system}
          {" · "}
          {copy?.acceptedImages(acceptedCount) ?? `${acceptedCount} accepted images`}
        </p>
        <a
          href={href}
          className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {copy?.viewGallery ?? "View gallery"}
          <ArrowRight
            className="size-4 transition-transform group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </a>
      </div>
    </article>
  );
}
